import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Badge,
  Spinner,
  Card,
  CardBody,
  Input,
  Button,
} from "reactstrap";
import { toast } from "react-toastify";
import { getMyEnquiriesApi } from "../../../api/apiUrlConst";

interface Enquiry {
  id: number;
  eid: string;
  name: string;
  email: string;
  phoneNumber: string;
  courseCategory: string;
  courseName: string;
  status: string;
  submittedAt: string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "New":
      return "secondary";
    case "Assigned":
      return "primary";
    case "InFollowUp":
      return "warning";
    case "Joined":
      return "success";
    case "NotInterested":
      return "danger";
    case "DND":
      return "dark";
    default:
      return "light";
  }
};

const MyEnquiries = () => {
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [filtered, setFiltered] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const navigate = useNavigate();
  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
  };

  const loadEnquiries = async () => {
    try {
      const data = await getMyEnquiriesApi();
      setEnquiries(data);
      setFiltered(data);
    } catch (error: any) {
      toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEnquiries();
  }, []);

  // 🔍 Filtering Logic
  useEffect(() => {
    let data = enquiries;

    if (statusFilter) {
      data = data.filter((e) => e.status === statusFilter);
    }

    if (search) {
      const searchLower = search.toLowerCase();

      data = data.filter((e) =>
        e.name.toLowerCase().includes(searchLower) ||
        e.eid.toLowerCase().includes(searchLower) ||
        e.courseCategory.toLowerCase().includes(searchLower) ||
        e.courseName.toLowerCase().includes(searchLower) ||
        new Date(e.submittedAt)
          .toLocaleDateString()
          .includes(searchLower)
      );
    }

    setFiltered(data);
  }, [search, statusFilter, enquiries]);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
      <div  style={{ background: "#f4f6fb", minHeight: "100vh" }}>
      {/* ===== HEADER ===== */}
      <div
        className="p-4 mb-4"
        style={{
          background: "linear-gradient(90deg, #374151, #093d92)",
          borderRadius: "16px",
          color: "#fff",
        }}
      >
        <h3 className="fw-bold mb-1">My Enquiries</h3>
        <small>Track and manage your assigned enquiries</small>
      </div>

      {/* ===== FILTER SECTION ===== */}
      <Card
        className="border-0 shadow-sm mb-4"
        style={{ borderRadius: "16px" }}
      >
        <CardBody>
          <div className="row g-3">
            <div className="col-md-4">
              <Input
                placeholder="🔍 Search Name, Course, EID, Date..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="rounded-pill"
              />
            </div>

            <div className="col-md-3">
              <Input
                type="select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-pill"
              >
                <option value="">All Status</option>
                <option>New</option>
                <option>Assigned</option>
                <option>InFollowUp</option>
                <option>Joined</option>
                <option>NotInterested</option>
                <option>DND</option>
              </Input>
            </div>
              {/* Clear Button */}
               <div className="col-md-3">
    <Button
      color="primary"
      className="rounded-pill"
      onClick={clearFilters}
      
    >
      Clear
    </Button>
  </div>
 
          </div>
        </CardBody>
      </Card>

      {/* ===== TABLE ===== */}
      <Card
        className="border-0 shadow-sm"
        style={{ borderRadius: "16px" }}
      >
        <CardBody>
          <Table
            responsive
            hover
            className="align-middle"
            style={{
              borderCollapse: "separate",
              borderSpacing: "0 8px",
            }}
          >
            <thead>
              <tr style={{ background: "#f4f6fb" }}>
                <th>EID</th>
                <th>Name</th>
                <th>Phone No</th>
                <th>Course Category</th>
                <th>Course Name</th>
                <th>Status</th>
                <th>Submitted</th>
              </tr>
            </thead>

            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No enquiries found
                  </td>
                </tr>
              )}

              {filtered.map((enq) => (
                <tr
                  key={enq.id}
                  style={{
                    background: "#fff",
                    cursor: "pointer",
                    boxShadow:
                      "0 2px 10px rgba(0,0,0,0.03)",
                  }}
                  onClick={() =>
                    navigate(`/coordinator/enquiries/${enq.eid}`)
                  }
                >
                  <td className="fw-bold text-primary">
                    {enq.eid}
                  </td>

                  <td>
                    <div>{enq.name}</div>
                  </td>

                  <td>{enq.phoneNumber}</td>

                  <td>
                    <div>{enq.courseCategory}</div>
                  </td>

                  <td>{enq.courseName}</td>

                  <td>
                    <Badge
                      color={getStatusColor(enq.status)}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "20px",
                      }}
                    >
                      {enq.status}
                    </Badge>
                  </td>

                  <td>
                    {new Date(
                      enq.submittedAt
                    ).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </div>
  );
};

export default MyEnquiries;